import React from 'react';
import { Wallet } from 'lucide-react';
import HandoffSettlementActions from './HandoffSettlementActions';
import PartnerCompletionReport from './PartnerCompletionReport';
import {
  computeHandoffSettlement,
  formatRsd,
  hasHandoffSettlementReport,
  isHandoffSettlementVisible,
  resolveHandoffPartnerRecord
} from '../../utils/handoffSettlementUtils';

const HandoffSettlementDetails = ({ job, currentCompanyId, partners = [], onArchived }) => {
  if (!isHandoffSettlementVisible(job, currentCompanyId)) return null;

  const partnerRecord = resolveHandoffPartnerRecord(job, partners, currentCompanyId);
  const commissionRate = partnerRecord?.commissionRate ?? 0;
  const settlement = computeHandoffSettlement(job, commissionRate);
  const showFinancials = hasHandoffSettlementReport(job);
  const partnerName =
    partnerRecord?.companyName ||
    partnerRecord?.name ||
    'Servisni partner';

  const profitClass = settlement.profit >= 0 ? 'text-green-400' : 'text-red-400';

  return (
    <div className="bg-gray-700 rounded border border-emerald-700/50">
      <div className="px-2 py-1.5 border-b border-emerald-800/40">
        <span className="flex items-center gap-1 text-xs font-semibold text-emerald-300">
          <Wallet className="h-3 w-3" />
          {showFinancials ? `Obračun partnera — ${partnerName}` : `Izveštaj partnera — ${partnerName}`}
        </span>
        <p className="text-[9px] text-gray-400 mt-0.5">
          {showFinancials
            ? `Kako je partner završio posao i podela zarade (provizija ${settlement.commissionRate}%).`
            : 'Podaci koje je partner uneo pri završetku posla.'}
        </p>
      </div>

      <div className="px-2 py-1.5 text-[10px] text-gray-300 space-y-1">
        <PartnerCompletionReport job={job} />

        {showFinancials && (
          <>
            <div className="flex justify-between gap-2">
              <span className="text-gray-400">Naplaćeno:</span>
              <span className="text-white font-medium">{formatRsd(settlement.serviceCharged)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-gray-400">Reversi / delovi:</span>
              <span>{formatRsd(settlement.reversiCost)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-gray-400">Dodatni materijali:</span>
              <span>{formatRsd(settlement.additionalMaterialsCost)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-gray-400">Amortizacija:</span>
              <span>{formatRsd(settlement.amortizationCost)}</span>
            </div>
            <div className="flex justify-between gap-2 border-t border-gray-600 pt-1">
              <span className="text-gray-400">Ukupni troškovi:</span>
              <span className="text-orange-300 font-medium">{formatRsd(settlement.totalCost)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-gray-300 font-semibold">Čista zarada:</span>
              <span className={`font-bold ${profitClass}`}>{formatRsd(settlement.profit)}</span>
            </div>
            <div className="flex justify-between gap-2 border-t border-gray-600 pt-1">
              <span className="text-gray-400">Udeo partnera ({settlement.commissionRate}%):</span>
              <span className="text-blue-300 font-medium">{formatRsd(settlement.partnerShare)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-gray-400">Udeo centralnog servisa:</span>
              <span className="text-emerald-300 font-semibold">{formatRsd(settlement.centralShare)}</span>
            </div>

            {job.report?.fiscalReceiptNumber && (
              <div className="flex justify-between gap-2 pt-1 border-t border-gray-600">
                <span className="text-gray-400">Fiskalni račun:</span>
                <span>{job.report.fiscalReceiptNumber}</span>
              </div>
            )}

            {Array.isArray(job.report?.usedSpareParts) && job.report.usedSpareParts.length > 0 && (
              <div className="pt-1 border-t border-gray-600">
                <span className="text-gray-400 block mb-0.5">Korišćeni delovi:</span>
                <ul className="space-y-0.5">
                  {job.report.usedSpareParts.map((part, idx) => (
                    <li key={part._id || part.code || idx} className="text-white">
                      {(part.name || part.code || 'Deo')}{' '}
                      {part.quantity ? `× ${part.quantity}` : ''}
                      {part.price != null
                        ? ` — ${formatRsd(Number(part.price) * (Number(part.quantity) || 1))}`
                        : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {commissionRate === 0 && (
              <p className="text-[9px] text-amber-300/90 pt-1">
                Provizija partnera nije podešena (0%). Podesite procenat u listi servisnih partnera.
              </p>
            )}
          </>
        )}

        <HandoffSettlementActions
          job={job}
          currentCompanyId={currentCompanyId}
          partners={partners}
          onArchived={onArchived}
        />
      </div>
    </div>
  );
};

export default HandoffSettlementDetails;
